import { CheckIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import {
  ButtonGroup,
  Editable,
  EditablePreview,
  EditableTextarea,
  Flex,
  IconButton,
  Input,
  Text,
  useEditableControls,
} from "@chakra-ui/react";
import { useEffect, useId, useRef, useState } from "react";

import { Schematisation, SchematisationRevision, ThreediModel } from "@nens/threedi/openapi-client";

import {
  SaveSchematisationDataType,
  SaveSchematisationRevisionDataType,
  SaveThreediModelDataType,
} from "./SchematisationsDetail";

type SchematisationEditableProps = {
  revision?: SchematisationRevision;
  attributename: string;
  schematisation?: Schematisation;
  threediModel?: ThreediModel;
  saveSchematisationData?: SaveSchematisationDataType;
  saveThreediModelData?: SaveThreediModelDataType;
  saveSchematisationRevisionData?: SaveSchematisationRevisionDataType;
  isMetaAttribute?: boolean;
};

export function SchematisationEditable({
  revision,
  attributename,
  schematisation,
  saveSchematisationData,
  saveThreediModelData,
  threediModel,
  saveSchematisationRevisionData,
  isMetaAttribute,
}: SchematisationEditableProps) {
  function EditableControls() {
    const { isEditing, getSubmitButtonProps, getCancelButtonProps, getEditButtonProps } =
      useEditableControls();

    return isEditing ? (
      <ButtonGroup justifyContent="center" size="sm">
        <IconButton icon={<CheckIcon />} {...getSubmitButtonProps()} ml={2} aria-label="check" />
        <IconButton icon={<CloseIcon />} {...getCancelButtonProps()} aria-label="close" />
      </ButtonGroup>
    ) : (
      <IconButton
        ml="auto"
        backgroundColor="transparent"
        size="sm"
        color="brand.analytic"
        icon={<EditIcon />}
        alignSelf="center"
        {...getEditButtonProps()}
        aria-label="edit"
      />
    );
  }

  // @ts-ignore (used ts-ignore because meta attributes are variable)
  const metaAttributeValue =
    //@ts-ignore
    isMetaAttribute && schematisation?.meta?.[attributename]
      ? //@ts-ignore
        schematisation.meta[attributename]
      : "";

  const textAreaRef = useRef(null);
  const [editableValue, setEditableValue] = useState("");
  const id = useId();

  useEffect(() => {
    let value = "";

    if (isMetaAttribute) {
      value = metaAttributeValue;
    } else if (schematisation) {
      value = `${schematisation[attributename as keyof Schematisation]}`;
    } else if (revision) {
      value = `${revision[attributename as keyof SchematisationRevision]} `;
    } else if (threediModel) {
      value = `${threediModel[attributename as keyof ThreediModel]}`;
    }

    setEditableValue(value);
  }, [attributename, isMetaAttribute, metaAttributeValue, revision, schematisation, threediModel]);

  const resizeTextArea = () => {
    const textCurrent = textAreaRef.current ? (textAreaRef.current as HTMLTextAreaElement) : null;
    if (!textCurrent) return;
    textCurrent.style.height = "inherit";
    if (textCurrent.scrollHeight) textCurrent.style.height = textCurrent.scrollHeight + "px";
  };
  useEffect(resizeTextArea, [editableValue]);

  const handleSubmit = (value: string) => {
    if (schematisation && saveSchematisationData) {
      saveSchematisationData(attributename, value, isMetaAttribute);
    } else if (threediModel && saveThreediModelData) {
      saveThreediModelData(attributename, value);
    } else if (revision && saveSchematisationRevisionData) {
      saveSchematisationRevisionData(attributename, value);
    }
  };

  return (
    <Editable
      textAlign="start"
      value={editableValue}
      onChange={setEditableValue}
      isPreviewFocusable={false}
      onSubmit={handleSubmit}
      submitOnBlur={false}
      backgroundColor="brand.grey.100"
      borderWidth="base"
      borderStyle="solid"
      borderColor="brand.glacier"
      borderRadius="4px"
    >
      <Flex>
        <EditablePreview whiteSpace="pre-wrap" mr={2} p={3} />
        <Input
          as={EditableTextarea}
          ref={textAreaRef}
          id={id}
          resize="none"
          overflowY="hidden"
          padding="2px"
          height="inherit"
          onFocus={resizeTextArea}
          rows={1}
        />
        <EditableControls />
      </Flex>
    </Editable>
  );
}

interface SchematisationFieldEditableProps {
  title: string;
}

export function SchematisationFieldEditable(props: SchematisationFieldEditableProps) {
  const { title } = props;
  const [value, setValue] = useState("");
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value);

  return (
    <>
      <Text mb="8px">{title}</Text>
      <Input
        value={value}
        onChange={handleChange}
        placeholder="Here is a sample placeholder"
        size="sm"
      />
    </>
  );
}
